import React, { useRef } from 'react';
import { arrayOf, elementType, shape, object, string, node, oneOfType } from 'prop-types';

import { CloseListener } from '@andes/common';

import { CoachMarks } from './coach-marks/CoachMarks';
import useMapConfig from './hooks/use-map-config';
import { List, Filters, Map, Zrp } from './desktop';
import { useMapView } from './hooks/use-map-view';
import { useSelectedItem } from './hooks/use-selected-item';
import { PolygonProvider } from './context/polygon-context';
import { POLYGON_EVENTS } from './lib';

const namespace = 'ui-search-layout--map';

const MapDesktopContainer = ({ itemComponent, results, drawCoachmark }) => {
  const mapRef = useRef(null);
  const { zrp, filters, newFacetedSearch, drawCoachmark: mapConfigDrawCoachmark } = useMapConfig();
  const { applyNewFilters, isLoading, fitMarkersInMap, fixedCoords, updateResults, showZrp, setError } = useMapView({
    results,
    mapRef,
  });
  const { selectedMarker, selectedResults, selectedResultsIndex, setSelectedMarker } = useSelectedItem();

  const isClusterSelected = selectedResults && selectedResults.length > 1;
  const handleItemHover = (index) => {
    setSelectedMarker(isClusterSelected ? selectedResultsIndex : index);
  };
  const coachmarkConfig = drawCoachmark || mapConfigDrawCoachmark;

  const handlePolygonError = () => {
    setError(true);
  };

  const handleClickOutside = () => {
    if (mapRef.current?.isDrawing?.()) {
      mapRef.current.setPolygonTogglerState?.(POLYGON_EVENTS.IDLE);
    }
  };

  return (
    <PolygonProvider>
      <div className={namespace}>
        <div className={`${namespace}__left-content`}>
          {showZrp ? (
            <Zrp title={zrp.title} description={zrp.message} />
          ) : (
            <List
              ItemComponent={itemComponent}
              results={selectedResults || results}
              isClusterSelected={isClusterSelected}
              onItemHover={handleItemHover}
              onUnselectCluster={() => setSelectedMarker(null, null)}
              isLoadingResults={isLoading}
            />
          )}
        </div>
        <div className={`${namespace}__right-content`}>
          <Filters
            isLoading={isLoading}
            filters={filters}
            newFacetedSearch={newFacetedSearch}
            onPrimaryFilterChange={applyNewFilters}
          />
          <CloseListener onCloseOutside={handleClickOutside}>
            <div className={`${namespace}__right-content--map`}>
              {coachmarkConfig && <CoachMarks {...coachmarkConfig} localStorageKey="drawCoachmark" />}
              <Map
                ref={mapRef}
                fitMarkersInMap={fitMarkersInMap}
                fixedCoords={fixedCoords}
                isLoadingResults={isLoading}
                markers={results}
                updateResults={updateResults}
                selectedMarker={selectedMarker}
                selectedCluster={selectedResultsIndex}
                setSelectedMarker={setSelectedMarker}
                onPolygonError={handlePolygonError}
              />
            </div>
          </CloseListener>
        </div>
      </div>
    </PolygonProvider>
  );
};

MapDesktopContainer.propTypes = {
  drawCoachmark: shape({
    steps: arrayOf(
      shape({
        title: string.isRequired,
        content: oneOfType([string, node]).isRequired,
        placement: string,
        target: string.isRequired,
      }),
    ).isRequired,
    locale: object,
  }),
  itemComponent: elementType.isRequired,
  results: arrayOf(shape()),
};

MapDesktopContainer.defaultProps = {
  drawCoachmark: undefined,
  results: [],
};

export default MapDesktopContainer;
